import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const LOGO_URL = '/RemaH.png';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Veuillez entrer votre adresse email');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
      toast.success('Email de réinitialisation envoyé !');
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        toast.error('Aucun compte trouvé avec cet email');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Adresse email invalide');
      } else {
        toast.error("Erreur lors de l'envoi. Réessayez.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-purple-950 relative overflow-hidden flex items-center justify-center px-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={LOGO_URL}
            alt="Remadia"
            className="w-56 h-56 mx-auto mb-4 object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
        </div>

        <Card className="p-8 bg-white/10 backdrop-blur-xl border-2 border-white/20 shadow-2xl rounded-3xl">
          {!sent ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Mot de passe oublié ?</h1>
                <p className="text-white/60 text-sm">
                  Entrez votre email et nous vous enverrons un lien de réinitialisation.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="reset-email" className="text-white mb-2 block">
                    Adresse email
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg hover:shadow-cyan-500/50 py-6 text-lg rounded-xl transition-all"
                  disabled={loading}
                >
                  {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Email envoyé !</h2>
              <p className="text-white/60 text-sm mb-2">
                Un lien de réinitialisation a été envoyé à :
              </p>
              <p className="text-cyan-400 font-semibold mb-6">{email}</p>
              <p className="text-white/50 text-xs mb-6">
                Vérifiez vos spams si vous ne trouvez pas l'email.
              </p>
              <Button
                onClick={() => {
                  setSent(false);
                  setEmail('');
                }}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10 rounded-xl"
              >
                Renvoyer à une autre adresse
              </Button>
            </div>
          )}

          {/* Retour connexion */}
          <button
            onClick={() => navigate('/')}
            className="mt-6 flex items-center justify-center gap-2 text-sm text-white/50 hover:text-white/80 transition-colors w-full"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </button>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
